import React, { useState } from 'react';
import { X, Upload, FileSpreadsheet, Check, AlertCircle, Loader2, Download } from 'lucide-react';
import * as XLSX from 'xlsx';
import { API_CONFIG } from '../../config/api.config';

interface ImportStudentsModalProps {
    isOpen: boolean;
    onClose: () => void;
    onImport: () => void; // trigger load lại danh sách
}

const API = API_CONFIG.python.apiUrl;

const getToken = () =>
    localStorage.getItem('jwt_token') || localStorage.getItem('auth_token') || localStorage.getItem('token') || '';

// Nhập danh sách học sinh từ Excel → /students/teacher/students/import-excel (DB CHUNG cho ebook & pmaker).
const ImportStudentsModal: React.FC<ImportStudentsModalProps> = ({ isOpen, onClose, onImport }) => {
    const [file, setFile] = useState<File | null>(null);
    const [previewData, setPreviewData] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [importing, setImporting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFile = e.target.files?.[0];
        if (selectedFile) {
            setFile(selectedFile);
            parseExcel(selectedFile);
        }
    };

    const parseExcel = (f: File) => {
        setLoading(true);
        setError(null);
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const data = e.target?.result;
                const workbook = XLSX.read(data, { type: 'binary' });
                const worksheet = workbook.Sheets[workbook.SheetNames[0]];
                const json = XLSX.utils.sheet_to_json(worksheet);
                const mapped = json.map((row: any) => ({
                    sbd: row['SBD'] || row['Số báo danh'] || row['ID'] || '',
                    class: row['Lớp'] || row['Class'] || '',
                    name: row['Họ tên'] || row['Họ và tên'] || row['Name'] || row['Full Name'] || '',
                    email: row['Email'] || row['Địa chỉ email'] || '',
                })).filter((item) => item.name);
                if (mapped.length === 0) throw new Error('Không tìm thấy dữ liệu học sinh hợp lệ.');
                setPreviewData(mapped);
            } catch (err: any) {
                setError(err.message || 'Lỗi đọc file Excel');
            } finally {
                setLoading(false);
            }
        };
        reader.readAsBinaryString(f);
    };

    const handleConfirmImport = async () => {
        if (!file) return;
        setImporting(true);
        setError(null);
        const formData = new FormData();
        formData.append('file', file);
        try {
            const response = await fetch(`${API}/students/teacher/students/import-excel`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${getToken()}` },
                body: formData,
            });
            if (!response.ok) {
                const errData = await response.json().catch(() => ({}));
                throw new Error(errData.detail || 'Lỗi upload file lên server');
            }
            const result = await response.json();
            alert(`Thành công! Thêm mới: ${result.created ?? 0}, Cập nhật: ${result.updated ?? 0}`);
            onImport();
            onClose();
            setFile(null);
            setPreviewData([]);
        } catch (err: any) {
            setError(err.message);
        } finally {
            setImporting(false);
        }
    };

    const downloadSample = () => {
        const sampleData = [
            { SBD: '120001', 'Lớp': '12A1', 'Họ tên': 'Nguyễn Văn A', Email: 'hocsinhA@gmail.com' },
            { SBD: '120002', 'Lớp': '12A1', 'Họ tên': 'Trần Thị B', Email: 'hocsinhB@yahoo.com' },
            { SBD: '110050', 'Lớp': '11B2', 'Họ tên': 'Lê Văn C', Email: 'hocsinhC@outlook.com' },
        ];
        const worksheet = XLSX.utils.json_to_sheet(sampleData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Danh sách học sinh');
        XLSX.writeFile(workbook, 'Mau_Danh_Sach_Hoc_Sinh.xlsx');
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm" onClick={onClose} />
            <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-3xl overflow-hidden flex flex-col max-h-[90vh]">
                <div className="bg-gradient-to-r from-emerald-600 to-teal-600 p-6 text-white relative">
                    <button onClick={onClose} className="absolute right-4 top-4 p-2 hover:bg-white/20 rounded-full transition-colors"><X size={20} /></button>
                    <div className="flex items-center gap-3 mb-2">
                        <div className="p-2 bg-white/20 rounded-xl"><FileSpreadsheet size={24} /></div>
                        <h2 className="text-xl font-bold">Nhập danh sách học sinh</h2>
                    </div>
                    <p className="text-emerald-100 text-sm font-medium opacity-90">Hỗ trợ .xlsx, .xls (SBD, Lớp, Họ tên, Email) — dùng chung với PMaker</p>
                </div>

                <div className="p-6 overflow-y-auto flex-1">
                    {!file ? (
                        <div className="border-2 border-dashed border-gray-200 rounded-2xl p-12 text-center">
                            <input type="file" id="excel-upload-ebook" className="hidden" accept=".xlsx, .xls" onChange={handleFileChange} />
                            <label htmlFor="excel-upload-ebook" className="cursor-pointer flex flex-col items-center">
                                <div className="w-16 h-16 bg-emerald-50 text-emerald-600 rounded-full flex items-center justify-center mb-4"><Upload size={32} /></div>
                                <h3 className="text-lg font-bold text-gray-900 mb-1">Tải lên file Excel</h3>
                                <p className="text-gray-500 text-sm mb-4">Nhấn để chọn file</p>
                                <div className="text-xs text-emerald-600 font-bold bg-emerald-50 px-3 py-1.5 rounded-lg border border-emerald-100">Cột yêu cầu: SBD, Lớp, Họ tên, Email</div>
                            </label>
                            <button onClick={downloadSample} className="mt-8 inline-flex items-center gap-2 text-sm font-bold text-gray-500 hover:text-blue-600 transition-colors">
                                <Download size={16} /> Tải file mẫu (.xlsx)
                            </button>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            <div className="flex items-center justify-between bg-emerald-50 p-4 rounded-2xl border border-emerald-100">
                                <div className="flex items-center gap-3">
                                    <FileSpreadsheet className="text-emerald-600" />
                                    <div>
                                        <div className="font-bold text-emerald-900 text-sm">{file.name}</div>
                                        <div className="text-xs text-emerald-600">Phát hiện {previewData.length} học sinh</div>
                                    </div>
                                </div>
                                <button onClick={() => { setFile(null); setPreviewData([]); }} className="text-emerald-600 hover:text-emerald-800 text-xs font-bold underline">Chọn lại</button>
                            </div>
                            {loading ? (
                                <div className="py-12 flex flex-col items-center gap-3"><Loader2 className="animate-spin text-emerald-600" size={32} /><span className="text-gray-500 font-medium">Đang xử lý...</span></div>
                            ) : error ? (
                                <div className="p-4 bg-red-50 text-red-600 rounded-xl flex items-center gap-3 border border-red-100"><AlertCircle size={20} /><span className="text-sm font-bold">{error}</span></div>
                            ) : (
                                <div className="border border-gray-100 rounded-xl overflow-hidden shadow-sm">
                                    <table className="w-full text-left text-xs">
                                        <thead className="bg-gray-50"><tr>
                                            <th className="px-3 py-2 font-bold text-gray-500 w-12 text-center">STT</th>
                                            <th className="px-3 py-2 font-bold text-gray-500">SBD</th>
                                            <th className="px-3 py-2 font-bold text-gray-500">Lớp</th>
                                            <th className="px-3 py-2 font-bold text-gray-500">Họ tên</th>
                                            <th className="px-3 py-2 font-bold text-gray-500">Email</th>
                                        </tr></thead>
                                        <tbody className="divide-y divide-gray-50">
                                            {previewData.slice(0, 10).map((hs, i) => (
                                                <tr key={i}>
                                                    <td className="px-3 py-2 text-center text-gray-400 font-medium">{i + 1}</td>
                                                    <td className="px-3 py-2 text-gray-700 font-mono">{hs.sbd}</td>
                                                    <td className="px-3 py-2 text-gray-700 font-bold">{hs.class}</td>
                                                    <td className="px-3 py-2 text-gray-900 font-bold">{hs.name}</td>
                                                    <td className="px-3 py-2 text-gray-600 font-medium">{hs.email || '--'}</td>
                                                </tr>
                                            ))}
                                            {previewData.length > 10 && (
                                                <tr><td colSpan={5} className="px-3 py-2 text-center text-gray-400 italic font-medium bg-gray-50/50">... và {previewData.length - 10} học sinh khác</td></tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <div className="p-6 bg-gray-50 border-t border-gray-100 flex gap-3">
                    <button onClick={onClose} className="flex-1 py-3 text-gray-500 font-bold hover:bg-gray-100 rounded-xl transition-colors">Hủy bỏ</button>
                    <button disabled={!file || previewData.length === 0 || loading || !!error || importing} onClick={handleConfirmImport}
                        className="flex-1 py-3 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded-xl font-bold transition-all shadow-lg shadow-emerald-200 active:scale-95 flex items-center justify-center gap-2">
                        {importing ? <Loader2 size={18} className="animate-spin" /> : <Check size={18} />} Xác nhận nhập danh sách
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ImportStudentsModal;
